import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { useMsal } from '@azure/msal-react';
import { subscribeRoleOverride } from '../utils/roleOverride';
import { subscribeProductsChanged } from '../utils/productCacheBus';
import {
  PRODUCT_OPTIONS as FALLBACK_PRODUCT_OPTIONS,
  PRODUCT_BASE_COSTS as FALLBACK_PRODUCT_BASE_COSTS,
  PRODUCT_PRICES as FALLBACK_PRODUCT_PRICES,
  PRODUCT_CONCENTRATIONS as FALLBACK_PRODUCT_CONCENTRATIONS,
  PRODUCT_DESCRIPTIONS as FALLBACK_PRODUCT_DESCRIPTIONS,
  PRODUCT_BLENDING_COSTS as FALLBACK_PRODUCT_BLENDING_COSTS,
  NANOPARTICLE_BASE_COST as NANOPARTICLE_BASE_COST_FALLBACK,
  SURFACTANT_MARGIN,
} from '../constants/pricing';
import { priceFromGrossMarginRate, resolveGrossMarginRate } from '../utils/marginResolution';

const ProductsContext = createContext();

export function ProductsProvider({ children }) {
  const { apiCall } = useApi();
  const { accounts } = useMsal();
  // Use a stable boolean primitive so MSAL token-refresh events (which produce a
  // new `accounts` array reference even though the account set hasn't changed)
  // don't invalidate the memoized callbacks and cascade re-renders to every
  // subscriber in the app. Mirrors the pattern in IngredientsContext.
  const isAuthenticated = accounts && accounts.length > 0;
  const [products, setProducts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLeadership, setIsLeadership] = useState(false);
  const [isSalesManager, setIsSalesManager] = useState(false);
  // True once /api/me has resolved (success or failure). Route guards use this to
  // avoid redirecting a legitimate user before their permissions have loaded.
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [canViewAllDeals, setCanViewAllDeals] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [moduleToggles, setModuleToggles] = useState({});
  const [userRole, setUserRole] = useState('user');
  const [userRoles, setUserRoles] = useState(['user']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  // When set to a YYYY-MM-DD date in the future, products are refetched with
  // ?at=<date> so the calculator sees prices that will be effective on that
  // date. Used by QuoteEditor when the chosen deal's expected_close_date is
  // past a scheduled price increase. Null = today's prices.
  const [pricingDate, setPricingDate] = useState(null);

  // Load user/permissions from /api/me. Kept separate from product loading so a
  // pricingDate change (deal-scoped pricing) doesn't needlessly re-fetch
  // permissions and double-toggle the shared loading flag. /api/me does not vary
  // by pricing date. Does NOT touch `loading` — that tracks product readiness.
  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const meResult = await apiCall('me');
      setIsAdmin(meResult.isAdmin || false);
      const roles = meResult.userRoles || [meResult.userRole || 'user'];
      setUserRoles(roles);
      setIsSalesManager(roles.includes('sales_manager'));
      setIsLeadership(meResult.isLeadership || roles.includes('leadership'));
      setCanViewAllDeals(meResult.canViewAllDeals || false);
      setPermissions(meResult.permissions || {});
      setModuleToggles(meResult.moduleToggles || {});
      setUserRole(meResult.userRole || 'user');
    } catch (err) {
      setError(err.message);
      // Keep using fallback values on error
    } finally {
      setPermissionsLoaded(true);
    }
  }, [apiCall, isAuthenticated]);

  const loadProducts = useCallback(async () => {
    // Only try to load if user is authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const productsQuery = pricingDate
        ? `blended-products-list?includeInactive=true&at=${encodeURIComponent(pricingDate)}`
        : 'blended-products-list?includeInactive=true';
      // products-list was deleted as part of the pricing refactor so
      // blended-products-list is the single source of truth for pricing data.
      const productsResult = await apiCall(productsQuery);
      setProducts(productsResult.products || []);
    } catch (err) {
      setError(err.message);
      // Keep using fallback values on error
    } finally {
      setLoading(false);
    }
  }, [apiCall, isAuthenticated, pricingDate]);

  // Initial bootstrap: load permissions + products once when authenticated.
  useEffect(() => {
    if (isAuthenticated && !initialized) {
      Promise.all([loadPermissions(), loadProducts()]).finally(() => setInitialized(true));
    }
  }, [isAuthenticated, initialized, loadPermissions, loadProducts]);

  // Refetch ONLY products when pricingDate changes (set by QuoteEditor for
  // deal-scoped pricing) — permissions don't depend on the date.
  useEffect(() => {
    if (!initialized) return;
    if (!isAuthenticated) return;
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingDate]);

  // Admin product edits are written through IngredientsContext, which keeps its
  // own copy of the same `blended_products` rows. Without this the two copies
  // diverge for the rest of the session — archiving a product removed it from the
  // admin table but left it selectable in the calculator / quote-builder dropdown
  // until a full page reload.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return subscribeProductsChanged(() => {
      loadProducts();
    });
  }, [isAuthenticated, loadProducts]);

  // Re-fetch ONLY permissions when the admin role-override changes so
  // permissions and role-gated UI reflect the chosen view-as role immediately.
  // Products don't vary by role, so they're left untouched.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return subscribeRoleOverride(() => {
      loadPermissions();
    });
  }, [isAuthenticated, loadPermissions]);

  // Computed values for calculator (matching existing constant shapes)
  // Only use active products for calculator
  const activeProducts = useMemo(() =>
    products.filter(p => p.is_active),
    [products]
  );

  const PRODUCT_OPTIONS = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_OPTIONS;
    }
    return activeProducts.map(p => ({ value: p.code, label: p.name }));
  }, [activeProducts]);

  // IBC cost per gallon for each product. Blended products: the IBC-quoted blend cost
  // (excludes nanoparticles — IBC pulls from Activ inventory). Simple products (NANO-RAW):
  // 0, since there's no IBC blending involved.
  // While DB products are loading, fall back to hardcoded base costs so the calculator
  // can show a real price for the default product instead of $0.
  const PRODUCT_IBC_COSTS = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_BASE_COSTS;
    }
    return activeProducts.reduce((acc, p) => ({
      ...acc,
      [p.code]: Number(p.ibc_cost_per_gallon) || 0,
    }), {});
  }, [activeProducts]);

  // Landed nano cost per gallon for each product, resolved from:
  //   blended products → product.nanoparticle (via nanoparticle_id)
  //   simple products → product.ingredient (via ingredient_id, e.g. NANO-RAW → NANO-STD)
  // Landed cost = cost_per_gallon + shipping_cost_per_gallon (shipping re-added in 20260415).
  const PRODUCT_NANO_COSTS = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_OPTIONS.reduce((acc, opt) => ({
        ...acc,
        [opt.value]: NANOPARTICLE_BASE_COST_FALLBACK,
      }), {});
    }
    return activeProducts.reduce((acc, p) => {
      const ing = p.nanoparticle || p.ingredient;
      const cost = Number(ing?.cost_per_gallon) || 0;
      const shipping = Number(ing?.shipping_cost_per_gallon) || 0;
      return { ...acc, [p.code]: cost + shipping };
    }, {});
  }, [activeProducts]);

  // Nano shipping per gallon (Nissan → IBC freight), broken out so the UI can
  // show it as its own line item instead of burying it inside landed cost.
  const PRODUCT_NANO_SHIPPING = useMemo(() => {
    if (activeProducts.length === 0) return {};
    return activeProducts.reduce((acc, p) => {
      const ing = p.nanoparticle || p.ingredient;
      return { ...acc, [p.code]: Number(ing?.shipping_cost_per_gallon) || 0 };
    }, {});
  }, [activeProducts]);

  // Product type flag for the calculator: 'blended' uses IBC + nano formula;
  // 'simple' (raw nano) uses nano-only formula.
  const PRODUCT_TYPES = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_OPTIONS.reduce((acc, opt) => ({
        ...acc,
        [opt.value]: 'blended',
      }), {});
    }
    return activeProducts.reduce((acc, p) => ({
      ...acc,
      [p.code]: p.product_type || 'blended',
    }), {});
  }, [activeProducts]);

  // Legacy lookups retained for backwards compatibility with code that hasn't migrated
  // off them (e.g. admin forms, PDF generation). The calculator itself now uses the
  // IBC/nano lookups above.
  const PRODUCT_BASE_COSTS = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_BASE_COSTS;
    }
    return activeProducts.reduce((acc, p) => {
      // Prefer ibc_cost_per_gallon (new canonical cost); fall back to legacy base_cost chain.
      let baseCost = Number(p.ibc_cost_per_gallon ?? p.base_cost ?? 0);
      if (!baseCost && p.surfactant) {
        baseCost = Number(p.surfactant.cost_per_gallon || 0);
      } else if (!baseCost && p.ingredient) {
        baseCost = Number(p.ingredient.cost_per_gallon || 0);
      }
      return { ...acc, [p.code]: baseCost };
    }, {});
  }, [activeProducts]);

  const PRODUCT_PRICES = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_PRICES;
    }
    return activeProducts.reduce((acc, p) => {
      // gross_margin_rate is a RATE: price = cost / (1 - rate). SURFACTANT_MARGIN
      // is the same kind of rate, so it is a safe fallback here. It was not
      // before this was normalized - it used to be divided in directly, which
      // priced the fallback path at 6.67x cost instead of 1.18x.
      let price;
      if (p.surfactant) {
        const totalCost = Number(p.surfactant.cost_per_gallon || 0);
        price = priceFromGrossMarginRate(totalCost, resolveGrossMarginRate(p.surfactant, null, SURFACTANT_MARGIN));
      } else if (p.ingredient) {
        const totalCost = Number(p.ingredient.cost_per_gallon || 0);
        price = priceFromGrossMarginRate(totalCost, resolveGrossMarginRate(p.ingredient, null, SURFACTANT_MARGIN));
      } else {
        price = priceFromGrossMarginRate(p.base_cost || 0, SURFACTANT_MARGIN);
      }
      return { ...acc, [p.code]: price };
    }, {});
  }, [activeProducts]);

  const PRODUCT_CONCENTRATIONS = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_CONCENTRATIONS;
    }
    return activeProducts.reduce((acc, p) => ({
      ...acc,
      [p.code]: p.concentration
    }), {});
  }, [activeProducts]);

  const PRODUCT_BLENDING_COSTS = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_BLENDING_COSTS;
    }
    return activeProducts.reduce((acc, p) => ({
      ...acc,
      [p.code]: Number(p.blending_cost_per_gallon) || 0
    }), {});
  }, [activeProducts]);

  const PRODUCT_DESCRIPTIONS = useMemo(() => {
    if (activeProducts.length === 0) {
      return FALLBACK_PRODUCT_DESCRIPTIONS;
    }
    return activeProducts.reduce((acc, p) => ({
      ...acc,
      [p.code]: p.description
    }), {});
  }, [activeProducts]);

  // Get product by code (useful for displaying product names in quotes)
  const getProductByCode = useCallback((code) => {
    return products.find(p => p.code === code) || null;
  }, [products]);

  // The actual date a scheduled price increase takes effect, as stamped by the
  // backend (priceSchedule.js `_priceEffectiveDate`) — distinct from `pricingDate`,
  // which is just the as-of query anchor (the deal's expected close date). Used by
  // QuoteEditor to show the real effective date in the "scheduled increase" banner.
  const priceEffectiveDate = useMemo(() => {
    if (!pricingDate) return null;
    let earliest = null;
    for (const p of products) {
      for (const date of [
        p._priceEffectiveDate,
        p.surfactant?._priceEffectiveDate,
        p.nanoparticle?._priceEffectiveDate,
        p.ingredient?._priceEffectiveDate,
      ]) {
        if (date && (!earliest || date < earliest)) earliest = date;
      }
    }
    return earliest;
  }, [products, pricingDate]);

  // True if the most recent fetch overlaid future-dated prices on at least one
  // product or nested ingredient. Used by QuoteEditor to decide whether to
  // show the "pricing reflects scheduled increase" banner.
  const hasFuturePricingApplied = useMemo(() => !!priceEffectiveDate, [priceEffectiveDate]);

  // Memoize the context value so that consumers only re-render when data
  // actually changes, not on every provider render (e.g. MSAL token refreshes
  // trigger parent re-renders that would otherwise create a new object reference
  // and cascade through every subscriber in the app).
  const value = useMemo(() => ({
    products,
    activeProducts,
    isAdmin,
    isLeadership,
    permissionsLoaded,
    isSalesManager,
    canViewAllDeals,
    permissions,
    moduleToggles,
    // A user is "quote builder only" if their only role is calculator
    isCalculator: userRoles.length === 1 && userRoles[0] === 'calculator',
    userRole,
    userRoles,
    loading,
    initialized,
    error,
    loadProducts,
    loadPermissions,
    getProductByCode,
    PRODUCT_OPTIONS,
    PRODUCT_BASE_COSTS,
    PRODUCT_PRICES,
    PRODUCT_CONCENTRATIONS,
    PRODUCT_BLENDING_COSTS,
    PRODUCT_DESCRIPTIONS,
    // New lookups for the IBC + nano-allocation pricing model
    PRODUCT_IBC_COSTS,
    PRODUCT_NANO_COSTS,
    PRODUCT_NANO_SHIPPING,
    PRODUCT_TYPES,
    pricingDate,
    setPricingDate,
    hasFuturePricingApplied,
    priceEffectiveDate,
  }), [
    products,
    activeProducts,
    isAdmin,
    isLeadership,
    permissionsLoaded,
    isSalesManager,
    canViewAllDeals,
    permissions,
    moduleToggles,
    userRole,
    userRoles,
    loading,
    initialized,
    error,
    loadProducts,
    loadPermissions,
    getProductByCode,
    PRODUCT_OPTIONS,
    PRODUCT_BASE_COSTS,
    PRODUCT_PRICES,
    PRODUCT_CONCENTRATIONS,
    PRODUCT_BLENDING_COSTS,
    PRODUCT_DESCRIPTIONS,
    PRODUCT_IBC_COSTS,
    PRODUCT_NANO_COSTS,
    PRODUCT_NANO_SHIPPING,
    PRODUCT_TYPES,
    pricingDate,
    setPricingDate,
    hasFuturePricingApplied,
    priceEffectiveDate,
  ]);

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProductsContext() {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProductsContext must be used within a ProductsProvider');
  }
  return context;
}
